import { BogeyState } from "../enum/BogeyState";
import { SPGameState } from "../enum/GameState";
import { SoundByte } from "../enum/sounds";
import MonoBehavior from "../lib/MonoBehavior";
import { Time } from "../lib/Time";
import Transform from "../lib/Transform";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import { lerp } from "../util";
import Color from "../util/Color";
import { Random } from "../util/Random";
import Explosion from "./Explosion";
import ExpOrb from "./ExpOrb";
import SoundManager from "./SoundManager";
import { GravitonPulseBehavior } from "./SPAbilities/GravitonPulse";
import SPBullet from "./SPBullet";
import SPGameManager from "./SPGameManager";
import SPPlayer from "./SPPlayer";
import SPTrail from "./SPTrail";

export default class SPBogey extends MonoBehavior {

    velocity: Vector2 = Vector2.zero;
    acceleration: Vector2 = Vector2.zero;
    friction: number = 0.5;
    accelerationForce: number = 64;
    size: number = 8;
    trail: SPTrail|undefined;
    targetOffset = Vector2.fromRadians(Random.float(-Math.PI, 0)).scale(128);
    fireRate = 0;
    hp = 1;
    maxHP = 1;
    xpReward = 1;
    isBoss = false;
    name = "Bogey";
    color = new Color(255,80,80);
    isInvuln = false;

    fireRateTimer = 0;

    isDead = false;

    get isEmped() {return this.empRecoveryTimer > 0;}
    empRecoveryTime = 3;
    empRecoveryTimer = 0;
    
    //private
    protected hpLerp = this.hp;
    protected state: BogeyState = BogeyState.Attack;
    protected wanderTarget: Vector2|undefined;

    changeState(state: BogeyState) {
        switch (state) {
            case BogeyState.Attack:
                
                break;
            case BogeyState.Wander:
                
                break;
        
            default:
                break;
        }

        this.state = state;
    }

    getPlayer() {
        return this.engine.GetGameObjectsBehaviors<SPPlayer>(SPPlayer)[0];
    }

    Start(): void {
        this.trail = new SPTrail(this.gameObject);
        this.trail.color = this.color;
    }

    /**
     * 
     * @param maxAngle max angle per second it should turn
     * @returns the current difference in angle between this bogey and the player
     */
    RotateTowardsPlayer(maxAngle: number) {
        const vt = this.transform.rotation;
        const vu = SPPlayer.Singleton.transform.position.sub(this.transform.position).normalized;
        let t = vt.angle < 0 ? vt.angle + 2*Math.PI : vt.angle;
        let u = vu.angle < 0 ? vu.angle + 2*Math.PI : vu.angle;

        if (Math.abs(u - (t+(2*Math.PI))) < Math.abs(u - t)) {
            t = t+(2*Math.PI);
        } else if (Math.abs((u+2*Math.PI) - t) < Math.abs(u - t)) {
            u = u+(2*Math.PI);
        }

        const l = lerp(t, u, maxAngle * Time.deltaTime);

        this.transform.rotation = Vector2.fromRadians(l);

        return Math.abs(u - t);
    }

    /**
     * 
     * @param maxAngle max angle per second it should turn
     * @returns the current difference in angle between this bogey and the player
     */
    RotateTowardsTarget(target: Vector2, maxAngle: number) {
        const vt = this.transform.rotation;
        const vu = target;
        const t = vt.angle < 0 ? vt.angle + 2*Math.PI : vt.angle;
        const u = vu.angle < 0 ? vu.angle + 2*Math.PI : vu.angle;
        const l = lerp(t, u, maxAngle * Time.deltaTime);

        this.transform.rotation = Vector2.fromRadians(l);

        return Math.abs(u - t);
    }

    Damage(amt: number, options: {
        bypassExp?: boolean;
        attacker?: SPBogey|SPPlayer;
    } = {}) {
        if (this.isDead || this.isInvuln) return;

        this.hp -= amt;

        if (this.hp <= 0) {
            this.hp = 0;
            this.Die();
        }
    }

    Die(crashedIntoPlayer: boolean = false) {
        if (this.isDead) return;
        this.isDead = true;
        const [,expl] = this.engine.NewGameObjectWithScript<Explosion>(Explosion, {
            position: this.transform.position,
            initialValues: {
                size: this.size * 2,
                color: this.color,
                w: this.size * 4
            }
        });
        SoundManager.Singleton.PlaySound(SoundByte.Explosion);

        if (crashedIntoPlayer) {
            this.getPlayer()?.Damage(1, {bypassExp: true, attacker: this});
        }

        for (let i = 0; i < this.xpReward; i++) {
            this.engine.NewGameObjectWithScript(ExpOrb, {
                position: this.transform.position.add(new Vector2(
                    Random.int(-this.size, this.size),
                    Random.int(-this.size, this.size)
                )),
                initialValues: {
                    amount: 1
                }
            });
        }

        this.engine.RemoveGameObject(this.gameObject);
    }

    ShootAt(target: Transform) {
        if (this.fireRateTimer > 0 || this.fireRate === 0) return;

        this.fireRateTimer = 1/this.fireRate;
        const spawnPos = this.transform.position.add(this.transform.forward.scale(this.size + 3));
        this.engine.NewGameObjectWithScript(SPBullet, {
            position: spawnPos,
            initialValues: {
                velocity:  this.velocity.add(this.transform.forward.scale(200)),
                damage: 1,
                isPlayerBullet: false,
                owner: this
            }
        });
        SoundManager.Singleton.PlaySound(SoundByte.EnemyFireGun);
    }

    AttackUpdate(player: SPPlayer) {

        if (player.isDead) {
            this.changeState(BogeyState.Wander);
            return;
        }

        if (this.fireRateTimer > 0) {
            this.fireRateTimer -= Time.deltaTime;

            if (this.fireRateTimer <= 0) this.fireRateTimer = 0;
        }

        const targetPos = player.transform.position.add(this.targetOffset);
        let targetRot = player.transform.position.sub(this.transform.position).normalized.angle;
        let myRot = this.transform.rotation.angle;

        if (targetRot < 0) targetRot += 2 * Math.PI;
        if (myRot < 0) myRot += 2 * Math.PI;
        
        const diff = Math.abs(targetRot - myRot);
        const lerpValue = lerp(myRot, targetRot, Math.PI * Time.deltaTime);

        this.transform.rotation = Vector2.fromRadians(lerpValue);

        if (Vector2.distance(player.transform.position, this.transform.position) <= 256 && diff <= 0.1745) {
            this.ShootAt(player.transform);
        }

        this.acceleration = targetPos.sub(this.transform.position).normalized.scale(this.accelerationForce);
    }

    WanderUpdate() {

        if (!this.getPlayer().isDead) {
            this.changeState(BogeyState.Attack);
        }

        if (!this.wanderTarget) {
            const rt = Random.float(0, 2 * Math.PI);
            const vec = Vector2.fromRadians(rt).scale(Random.float(64, 256));
            this.wanderTarget = this.transform.position.add(vec);
        }

        if (Vector2.distance(this.wanderTarget, this.transform.position) <= this.size) {
            this.wanderTarget = undefined;
            return;
        }
        this.transform.rotation = this.velocity.normalized;
        this.acceleration = this.wanderTarget.sub(this.transform.position).normalized.scale(this.accelerationForce);
    }

    Update(): void {
        const player = this.getPlayer();

        if (!player || ![
            SPGameState.InGame,
            SPGameState.GameOver
        ].includes(SPGameManager.Singleton.state)) return;

        if (this.isEmped) {
            this.empRecoveryTimer -= Time.deltaTime;

            if (this.empRecoveryTimer <= 0) {
                this.empRecoveryTimer = 0;
            }
        }

        switch (this.state) {
            case BogeyState.Attack:
                if (!this.isEmped) this.AttackUpdate(player);
                break;
            case BogeyState.Wander:
                this.WanderUpdate();
                break;
        
            default:
                break;
        }

        //get sucked into gravity wells
        for (const well of this.engine.GetGameObjectsBehaviors<GravitonPulseBehavior>(GravitonPulseBehavior)) {
            const vec = well.transform.position.sub(this.transform.position);
            const dist = vec.magnitude;
            vec.normalize();
            if (dist <= this.size + well.size) {
                this.velocity = this.velocity.add(vec.scale(100 * Time.deltaTime));
            }
        }

        //avoid other bogeys
        for (const bogey of this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey)) {
            if (bogey.id === this.id) continue;

            const dist = Vector2.distance(this.transform.position, bogey.transform.position);

            if (dist < 48) {
                const vec = this.transform.position.sub(bogey.transform.position).normalized;
                this.acceleration = vec.scale(this.accelerationForce*2);
                break;
            }
        }

        if (Physics.CircleCircleCollision({
            position: this.transform.position,
            radius: this.size
        }, {
            position: player.transform.position,
            radius: player.size
        }) !== false && !player.isDead) {
            this.Die(true);
            return;
        }

        if (this.isEmped) this.acceleration = Vector2.zero;
        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));

        
    }

    LateUpdate(): void {
        this.trail?.LateUpdate();
        this.hpLerp = lerp(this.hpLerp, this.hp, Time.deltaTime * 4);
    }

    DrawBossBar() {
        const bossList = this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey).filter(
            v => v.isBoss
        );

        const bossIndex = bossList.map(v => v.id).indexOf(this.id);
        const yOffset = bossIndex * 16;

        const drawPos = new Vector2(64, this.engine.height - 64 - yOffset);

        this.ctx.fillStyle = '#ff555555';
        this.ctx.fillRect(drawPos.x, drawPos.y, this.engine.width - 128, 8);
        this.ctx.fillStyle = '#ffff55';
        this.ctx.fillRect(drawPos.x, drawPos.y, (this.engine.width - 128) * (this.hpLerp/this.maxHP), 8);
        this.ctx.fillStyle = '#ff5555';
        this.ctx.fillRect(drawPos.x, drawPos.y, (this.engine.width - 128) * (this.hp/this.maxHP), 8);
        this.engine.drawTexts([`${this.name}`], drawPos, {
            align: 'left',
            baseline: 'bottom',
            color: 'white'
        });
    }
    
    Draw(): void {
        this.trail?.Draw();

        this.ctx.strokeStyle = this.color.toHex();
        this.ctx.fillStyle = 'black';
        this.ctx.lineWidth = 1;
        this.engine.fillShape(
            this.transform.position.add(this.transform.forward.scale(this.size)),
            this.transform.position.add(this.transform.right.scale(this.size)),
            this.transform.position.add(this.transform.back.scale(this.size)),
            this.transform.position.add(this.transform.left.scale(this.size))
        );
        this.engine.strokeShape(
            this.transform.position.add(this.transform.forward.scale(this.size)),
            this.transform.position.add(this.transform.right.scale(this.size)),
            this.transform.position.add(this.transform.back.scale(this.size)),
            this.transform.position.add(this.transform.left.scale(this.size))
        );


        if (this.isBoss) {

            this.DrawBossBar();
        }
    }
}